import { useFormContext } from 'react-hook-form'
import { TextField } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next'
import { AddressFields, AddressProps } from 'components/inputs/address/types'
import { LETTERS_NUMBERS_SLASHES_AND_DASHES } from 'constants/regex'
import { getFieldError, getFieldName } from 'components/inputs/utils'
import PostalCodeAndTown from './postal-code-and-town/PostalCodeAndTown'

/**
 * Address component
 * @remarks: This component is used to render the address fields in a form. The inputs required for the RHF are:
 * - streetName
 * - streetNumber
 * - streetBox
 * - postalCode
 * - townName
 * - townCode
 *
 * @param {AddressProps} param0
 */
const Address = ({ parentDotPath, required = true }: AddressProps) => {
  // React Hook Form
  const {
    formState: { errors },
    register
  } = useFormContext()

  // i18n
  const { t } = useTranslation(['commonFormFields', 'validation'])

  return (
    <>
      <TextField
        {...register(getFieldName(AddressFields.STREET_NAME, parentDotPath), {
          required: {
            value: required,
            message: t('validation:required')
          }
        })}
        error={getFieldError(AddressFields.STREET_NAME, parentDotPath, errors)?.message}
        grid="span 2"
        label={t('commonFormFields:streetName.label')}
      />

      <TextField
        {...register(getFieldName(AddressFields.STREET_NUMBER, parentDotPath), {
          maxLength: {
            value: 5,
            message: t('validation:max.string', {
              attribute: t('commonFormFields:streetNumber.label'),
              length: 5
            })
          },
          pattern: {
            value: LETTERS_NUMBERS_SLASHES_AND_DASHES,
            message: t('commonFormFields:streetNumber.errors.invalid')
          },
          required: {
            value: required,
            message: t('validation:required')
          }
        })}
        error={getFieldError(AddressFields.STREET_NUMBER, parentDotPath, errors)?.message}
        grid="span 1"
        label={t('commonFormFields:streetNumber.label')}
      />

      <TextField
        {...register(getFieldName(AddressFields.STREET_BOX, parentDotPath), {
          maxLength: {
            value: 5,
            message: t('validation:max.string', {
              attribute: t('commonFormFields:streetBox.label'),
              length: 5
            })
          }
        })}
        error={getFieldError(AddressFields.STREET_BOX, parentDotPath, errors)?.message}
        grid="span 1"
        label={t('commonFormFields:streetBox.label')}
      />

      <PostalCodeAndTown {...{ parentDotPath, required }} />
    </>
  )
}

export default Address
